/**
 * Who AuditPlus may email (owner ruling 2026-10-18, golive-review G2):
 * company addresses only — @selfstorageplus.com or @arc.land (arc.land
 * added by the owner 2026-10-17) — one address per field, at most
 * MAX_EMAIL_RECIPIENTS per email. Pure, shared by the app, the server
 * (api/src/writePolicy.ts) and mirrored by every flow (docs/flows.md).
 *
 * OUTSIDE-EMAIL ALLOWLIST (owner rulings 2026-10-19): admins may allow a
 * whole outside domain or one outside address, each with a note, no
 * expiry (EmailAllowlist list). It applies ONLY to a task comment's
 * "also email" picks, scheduled report recipients and Admin-managed
 * completion notice recipients — task assignees,
 * DM copies and every alert stay company-only. Public mail domains can't
 * be allowed whole (a single address there can). Exact domains only:
 * "vendor.com" doesn't allow "x@sub.vendor.com".
 */
export const INTERNAL_EMAIL_DOMAINS = ['selfstorageplus.com', 'arc.land'] as const;

/** "@selfstorageplus.com or @arc.land" for messages. */
export const INTERNAL_DOMAINS_LABEL = INTERNAL_EMAIL_DOMAINS.map((d) => `@${d}`).join(' or ');

/** Most people one notification email goes to (To + Cc). */
export const MAX_EMAIL_RECIPIENTS = 10;

/** A task comment's extra "also email" picks: the comment's own recipient takes one of the ten. */
export const MAX_NOTIFY_EXTRA = MAX_EMAIL_RECIPIENTS - 1;

/**
 * Exactly one "@", a company domain, and no list separators or whitespace
 * anywhere (they could smuggle a second address into an email's To line).
 */
export function isInternalEmail(email: string): boolean {
  const e = email.trim().toLowerCase();
  if (!/^[^\s@;,]+@[^\s@;,]+$/.test(e)) return false;
  return INTERNAL_EMAIL_DOMAINS.some((d) => e.endsWith(`@${d}`));
}

/** TaskComments.NotifyUpns ("a;b;c") → its addresses, trimmed, blanks dropped. */
export function splitNotify(raw: unknown): string[] {
  return typeof raw === 'string' ? raw.split(';').map((u) => u.trim()).filter(Boolean) : [];
}

/** What's wrong with a comment's "also email" list (null = fine). */
export function notifyProblem(upns: readonly string[], allow: EmailAllowlist = EMPTY_ALLOWLIST): string | null {
  const outside = upns.find((u) => !isAllowedEmail(u, allow));
  if (outside !== undefined) return `${outside} can’t be emailed — use ${allowedLabel(allow)}.`;
  if (new Set(upns.map((u) => u.toLowerCase())).size > MAX_NOTIFY_EXTRA) {
    return `At most ${MAX_NOTIFY_EXTRA} extra people can be emailed about one comment.`;
  }
  return null;
}

/**
 * Free-mail providers: allowing one of these WHOLE would let anybody
 * receive AuditPlus mail. Mirrored in Flows 2c and 4 (flowPackages.test.ts).
 */
export const PUBLIC_MAIL_DOMAINS = [
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
  'yahoo.com', 'ymail.com', 'aol.com', 'icloud.com', 'me.com', 'mac.com',
  'proton.me', 'protonmail.com', 'gmx.com', 'gmx.net', 'mail.com', 'yandex.com',
  'zoho.com', 'comcast.net', 'verizon.net', 'att.net', 'sbcglobal.net',
] as const;

/** Allowed outside recipients (normalized: lower case, no spaces, domains without "@"). */
export interface EmailAllowlist {
  domains: readonly string[];
  addresses: readonly string[];
  /** Server only: the list couldn't be read just now (company addresses still pass; outside ones get "try again"). */
  unavailable?: boolean;
}

export const EMPTY_ALLOWLIST: EmailAllowlist = { domains: [], addresses: [] };

const DOMAIN = /^(?=.{3,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;
// Not starting with "." — the flows drop such entries too (parity).
const ADDRESS = /^[^\s@;,.][^\s@;,]{0,63}@[^\s@;,]+$/;

/** One allowlist entry as typed → its normalized form, or a reason it can't be added. */
export function parseAllowEntry(raw: string): { kind: 'domain' | 'address'; value: string } | { problem: string } {
  const v = raw.trim().toLowerCase().replace(/^@/, '');
  if (v === '') return { problem: 'Type a domain (vendor.com) or one address (jane@vendor.com).' };
  if (v.includes('@')) {
    const domain = v.slice(v.indexOf('@') + 1);
    if (!ADDRESS.test(v) || !DOMAIN.test(domain)) return { problem: `${raw.trim()} isn’t one email address.` };
    if (isInternalEmail(v)) return { problem: 'Company addresses are always allowed.' };
    return { kind: 'address', value: v };
  }
  if (!DOMAIN.test(v)) return { problem: `${raw.trim()} isn’t a domain (e.g. vendor.com).` };
  if ((INTERNAL_EMAIL_DOMAINS as readonly string[]).includes(v)) return { problem: 'Company domains are always allowed.' };
  if ((PUBLIC_MAIL_DOMAINS as readonly string[]).includes(v)) {
    return { problem: `${v} is a public mail service — add the one address instead (e.g. jane@${v}).` };
  }
  return { kind: 'domain', value: v };
}

/**
 * A stored entry that counts: exactly as the app/server store it (valid,
 * lower case, no spaces, no "@" before a domain). Anything else — e.g.
 * "@vendor.com" or "Vendor.com" typed straight into SharePoint — is
 * ignored, the same way the flows ignore it (they compare the raw value),
 * and Admin → Notifications shows it as "not valid" so it can be re-added.
 */
export function storedEntry(e: unknown): { kind: 'domain' | 'address'; value: string } | null {
  if (typeof e !== 'string') return null;
  const p = parseAllowEntry(e);
  return 'problem' in p || p.value !== e ? null : p;
}

/** The usable allowlist from stored entries (storedEntry rule). */
export function buildAllowlist(entries: readonly unknown[]): EmailAllowlist {
  const domains = new Set<string>();
  const addresses = new Set<string>();
  for (const e of entries) {
    const p = storedEntry(e);
    if (!p) continue;
    (p.kind === 'domain' ? domains : addresses).add(p.value);
  }
  return { domains: [...domains], addresses: [...addresses] };
}

/** A company address, or one outside address the allowlist allows. */
export function isAllowedEmail(email: string, allow: EmailAllowlist = EMPTY_ALLOWLIST): boolean {
  if (isInternalEmail(email)) return true;
  const e = email.trim().toLowerCase();
  if (!/^[^\s@;,]+@[^\s@;,]+$/.test(e)) return false;
  return allow.addresses.includes(e) || allow.domains.includes(e.slice(e.indexOf('@') + 1));
}

/** "@selfstorageplus.com or @arc.land" — plus "or an allowed outside address" when the allowlist has entries. */
export function allowedLabel(allow: EmailAllowlist = EMPTY_ALLOWLIST): string {
  return allow.domains.length + allow.addresses.length > 0
    ? `a company address (${INTERNAL_DOMAINS_LABEL}) or an allowed outside address`
    : `a company address (${INTERNAL_DOMAINS_LABEL})`;
}

/**
 * A Phase 7 simulation account (docs/simulation.md) — the personas, the
 * seeder, the [SIM] Stores mailbox and its plus addresses. A SIM account
 * may only address other SIM accounts (security review 2026-10-21: an
 * unattended persona agent must not be able to mail a real employee
 * through the app). Removed with the simulation in Phase 8.
 */
export function isSimAccount(address: string): boolean {
  return /^sim-auditplus-[a-z0-9.+-]*@selfstorageplus\.com$/i.test(address.trim());
}
